import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAppSelector } from '@/store';
import { selectUserRole } from '@/store/slices/authSlice';
import { normalizeRole, type EffectiveRole } from '@/shared/utils/roles';
import { usePermissions } from '@/shared/contexts/PermissionsContext';
import { shouldEnforcePagePermissions } from '@/shared/utils/pageAccess';
import AccessDenied from '@/shared/components/AccessDenied';

interface ProtectedRouteProps {
  children: React.ReactNode;
  isAuthenticated: boolean;
  requiredRoles?: EffectiveRole[];
  /** Permissions Manager page key — enforced for engineers only */
  pageKey?: string;
}

/**
 * ProtectedRoute
 * - Requires auth
 * - Optional hardcoded requiredRoles (managers/admins)
 * - Optional pageKey: for engineers, uses dynamic page_permissions (no fallback redirect)
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  isAuthenticated,
  requiredRoles,
  pageKey,
}) => {
  const location = useLocation();
  const role = useAppSelector(selectUserRole);
  const effectiveRole = normalizeRole(role);
  const { canAccessPage, isPermissionsReady, isPageAccessLoading } = usePermissions();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRoles && !requiredRoles.includes(effectiveRole)) {
    return <AccessDenied />;
  }

  // Engineers: enforce Permissions Manager page access — deny with no fallback
  if (pageKey && shouldEnforcePagePermissions(effectiveRole)) {
    if (!isPermissionsReady || isPageAccessLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }

    if (!canAccessPage(pageKey)) {
      return <AccessDenied />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
